import { onUnmounted, ref, watch, type Ref } from 'vue';

interface Timing {
  // How long `active` must hold before it shows, so a fast load shows nothing.
  delayMs: number;
  // Once shown, how long it stays, so a slow load's bar doesn't flicker off.
  holdMs: number;
}

export function useDelayedVisible(active: Ref<boolean>, { delayMs, holdMs }: Timing): Ref<boolean> {
  const visible = ref(false);
  let shownAt = 0;
  let showTimer: ReturnType<typeof setTimeout> | null = null;
  let hideTimer: ReturnType<typeof setTimeout> | null = null;

  const clearTimers = (): void => {
    if (showTimer) clearTimeout(showTimer);
    if (hideTimer) clearTimeout(hideTimer);
    showTimer = null;
    hideTimer = null;
  };

  const show = (): void => {
    shownAt = Date.now();
    visible.value = true;
    showTimer = null;
  };

  const hide = (): void => {
    visible.value = false;
    hideTimer = null;
  };

  const sync = (on: boolean): void => {
    clearTimers();
    if (on) {
      if (!visible.value) showTimer = setTimeout(show, delayMs);
      return;
    }
    if (!visible.value) return;
    const left = holdMs - (Date.now() - shownAt);
    if (left <= 0) hide();
    else hideTimer = setTimeout(hide, left);
  };

  watch(active, sync, { immediate: true });
  onUnmounted(clearTimers);
  return visible;
}
